import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import {
  ArrowRight,
  BadgeCheck,
  BriefcaseBusiness,
  CheckCircle2,
  Home,
  Loader2,
  LocateFixed,
  Mail,
  MapPin,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import { PublicNav } from "@/components/site/PublicNav";
import { Footer } from "@/components/site/Footer";
import { AskShieldsChat } from "@/components/chat/AskShieldsChat";
import {
  checkAreaAvailability,
  listAreaStatus,
  type AreaAvailabilityResult,
} from "@/lib/areas.functions";

type Audience = "customer" | "partner";
const AREA_HANDOFF_KEY = "shields:area-handoff";
const VERIFIED_SIGNUP_KEY = "shields:verified-area-signup";

type AreaHandoff = { audience: Audience; postcode: string; email: string; result: AreaAvailabilityResult };
function readAreaHandoff(): AreaHandoff | null {
  if (typeof window === "undefined") return null;
  try {
    const value = JSON.parse(window.sessionStorage.getItem(AREA_HANDOFF_KEY) ?? "null") as AreaHandoff | null;
    window.sessionStorage.removeItem(AREA_HANDOFF_KEY);
    return value && (value.audience === "customer" || value.audience === "partner") ? value : null;
  } catch { return null; }
}

export const Route = createFileRoute("/availability")({
  validateSearch: (search: Record<string, unknown>): { path?: Audience } =>
    search["path"] === "partner"
      ? { path: "partner" }
      : search["path"] === "customer"
        ? { path: "customer" }
        : {},
  head: () => ({
    meta: [
      { title: "Check your area — Shields" },
      { name: "description", content: "Check whether Shields is live in your postcode or register your area for launch." },
    ],
  }),
  component: AvailabilityPage,
});

function AvailabilityPage() {
  const { path } = Route.useSearch();
  const [handoff] = useState<AreaHandoff | null>(() => readAreaHandoff());
  const [audience, setAudience] = useState<Audience>(handoff?.audience ?? path ?? "customer");
  const [postcode, setPostcode] = useState(handoff?.postcode ?? "");
  const [email, setEmail] = useState(handoff?.email ?? "");
  const [result, setResult] = useState<AreaAvailabilityResult | null>(handoff?.result ?? null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const areas = useQuery({ queryKey: ["public-area-status"], queryFn: () => listAreaStatus() });

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const next = await checkAreaAvailability({ data: { postcode, email, audience } });
      setResult(next);
      if (next.kind === "invalid") setError(next.message);
      if (next.kind === "live" && typeof window !== "undefined") {
        window.sessionStorage.setItem(VERIFIED_SIGNUP_KEY, JSON.stringify({ audience, postcode: next.postcode, email, checkedAt: Date.now() }));
      }
      await areas.refetch();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "We could not check your postcode. Try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="public-cinematic min-h-screen bg-background text-[#071a3d]">
      <PublicNav />
      <main className="area-page">
        <section className="area-hero">
          <div className="area-hero-copy">
            <p className="area-kicker"><LocateFixed aria-hidden /> LIVE AREA CHECK</p>
            <h1>{result?.kind === "waitlist" ? "Shields hasn’t launched in your postcode yet." : "Is Shields live where you are?"}</h1>
            <p>
              Enter a real UK postcode. Shields securely geocodes it, checks the distance from every live area,
              and sends you down the correct Customer or Partner path.
            </p>
          </div>
          <div className="area-live-beacon" aria-label="Shields launch network status">
            <span><i /> NETWORK LIVE</span>
            <strong>{(areas.data ?? []).filter((area) => area.status === "live").length}</strong>
            <small>live launch area</small>
          </div>
        </section>

        <div className="area-layout">
          <section className="area-glass-card area-checker" aria-labelledby="area-check-title">
            <div className="area-section-heading">
              <span className="area-icon area-icon-gold"><ShieldCheck aria-hidden /></span>
              <div>
                <p>CHOOSE YOUR PATH</p>
                <h2 id="area-check-title">Start with what you need</h2>
              </div>
            </div>

            <div className="area-paths" role="radiogroup" aria-label="How you want to use Shields">
              <button type="button" role="radio" aria-checked={audience === "customer"} onClick={() => { setAudience("customer"); setResult(null); }} className={audience === "customer" ? "is-active" : ""}>
                <span className="area-icon area-icon-blue"><Home aria-hidden /></span>
                <span><strong>I need services</strong><small>For my home, property or project</small></span>
                <CheckCircle2 aria-hidden />
              </button>
              <button type="button" role="radio" aria-checked={audience === "partner"} onClick={() => { setAudience("partner"); setResult(null); }} className={audience === "partner" ? "is-active" : ""}>
                <span className="area-icon area-icon-gold"><BriefcaseBusiness aria-hidden /></span>
                <span><strong>I’m a service professional</strong><small>For work, customers and my business</small></span>
                <CheckCircle2 aria-hidden />
              </button>
            </div>

            <form onSubmit={submit} className="area-form">
              <label>
                <span>UK postcode</span>
                <span className="area-field"><MapPin aria-hidden /><input value={postcode} onChange={(event) => setPostcode(event.target.value.toUpperCase())} autoComplete="postal-code" placeholder="BT26 6AG" required minLength={5} maxLength={10} /></span>
              </label>
              <label>
                <span>Email address</span>
                <span className="area-field"><Mail aria-hidden /><input value={email} onChange={(event) => setEmail(event.target.value)} type="email" autoComplete="email" autoCapitalize="none" spellCheck={false} placeholder="you@example.com" required /></span>
              </label>
              <button type="submit" className="area-primary-action" disabled={busy}>
                {busy ? <Loader2 className="animate-spin motion-reduce:animate-none" aria-hidden /> : <LocateFixed aria-hidden />}
                {busy ? "Checking securely…" : "Check my area"}
                {!busy ? <ArrowRight aria-hidden /> : null}
              </button>
              <p><ShieldCheck aria-hidden /> Your email is only kept if your area has not launched, so Shields can record demand and notify you.</p>
            </form>

            {error ? <div role="alert" className="area-result area-result-error"><strong>Check the details</strong><span>{error}</span></div> : null}
            {result?.kind === "live" ? <LiveResult result={result} audience={audience} /> : null}
            {result?.kind === "waitlist" ? <WaitlistResult result={result} /> : null}
          </section>

          <aside className="area-side">
            <section className="area-glass-card">
              <div className="area-section-heading">
                <span className="area-icon area-icon-success"><BadgeCheck aria-hidden /></span>
                <div><p>LIVE AREA STATUS</p><h2>Shields launch network</h2></div>
              </div>
              <div className="area-status-list">
                {areas.isLoading ? <p className="area-muted">Loading verified areas…</p> : null}
                {(areas.data ?? []).map((area) => (
                  <article key={area.slug}>
                    <span className={area.status === "live" ? "area-status-dot is-live" : "area-status-dot"} aria-hidden />
                    <div>
                      <strong>{area.name}</strong>
                      <small>{area.sourcePostcode} · {area.radiusMiles} mile coverage</small>
                      <small>{area.latitude.toFixed(6)}, {area.longitude.toFixed(6)}</small>
                    </div>
                    <b>{area.status}</b>
                  </article>
                ))}
              </div>
              <p className="area-source-note">Coordinates are verified from UK postcode data. Distance checks use the submitted postcode’s latitude and longitude.</p>
            </section>
          </aside>
        </div>

        <section className="area-concierge" aria-labelledby="area-ai-title">
          <div className="area-concierge-copy">
            <p><Sparkles aria-hidden /> SHIELDS AI CONCIERGE</p>
            <h2 id="area-ai-title">Questions before Shields reaches your area?</h2>
            <span>Ask about joining, how matching works, or what Customers and Partners can do inside Shields.</span>
          </div>
          <AskShieldsChat compact audience={audience} className="area-concierge-chat" />
        </section>
      </main>
      <Footer />
    </div>
  );
}

function LiveResult({ result, audience }: { result: Extract<AreaAvailabilityResult, { kind: "live" }>; audience: Audience }) {
  return (
    <div className="area-result area-result-live" role="status">
      <span className="area-icon area-icon-success"><CheckCircle2 aria-hidden /></span>
      <div>
        <p>SHIELDS IS LIVE FOR THIS POSTCODE</p>
        <strong>{result.area.name}</strong>
        <small>{result.postcode} is {result.distanceMiles} miles from the verified launch centre.</small>
      </div>
      <a href={result.nextPath}>Create {audience === "partner" ? "Partner" : "Customer"} account <ArrowRight aria-hidden /></a>
    </div>
  );
}

function WaitlistResult({ result }: { result: Extract<AreaAvailabilityResult, { kind: "waitlist" }> }) {
  return (
    <div className="area-result area-result-waitlist" role="status">
      <p>AREA REQUEST RECORDED</p>
      <h3>Shields hasn’t launched in {result.locationName} yet.</h3>
      <span>We’ve securely recorded your interest for {result.postcode}. We’ll contact you when Shields becomes available in your area.</span>
    </div>
  );
}
